import * as d3 from 'd3';
import Fuse from 'fuse.js';
import type React from 'react';
import { type ChangeEvent, type MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Select, { type StylesConfig } from 'react-select';
import { isSpacerMapNode, rectNode } from '@/components/CourseMap/course-utils';
import type { NodeEntity } from '@/components/CourseMap/coursemap.types';
import useOutsideClick from '@/hooks/useOutsideClick';
import type { DisciplineWithCourses } from '@/lib/types';
import useCourseMapStore from '@/stores/courseMapStore';
import { themeSelectStyles } from '@/utils';

import { StyledAutoComplete, StyledWrapper } from './MapFilters.Styles';

interface MapFiltersProps {
	dropdownOptions?: DisciplineWithCourses[];
}

function mapCourseCardEl(tracker: string): HTMLElement | null {
	if (!tracker) return null;
	const cls = `course-${tracker}`;
	try {
		return document.querySelector(`#course-map .${CSS.escape(cls)}`) as HTMLElement | null;
	} catch {
		return null;
	}
}

/** D3 registers collapse/expand on the *empty* `<g>` in the first SVG; React cards sit in the overlay. */
function mapD3NodeEl(tracker: string): SVGGElement | null {
	if (!tracker) return null;
	const root = document.getElementById('course-map');
	if (!root) return null;
	const d3Svg = root.querySelector(':scope > svg');
	if (!d3Svg) return null;
	return (
		Array.from(d3Svg.querySelectorAll<SVGGElement>('[data-d3-tracker]')).find(
			(el) => el.getAttribute('data-d3-tracker') === tracker
		) ?? null
	);
}

const MapFilters = ({ dropdownOptions }: MapFiltersProps) => {
	const courseListRef = useRef<HTMLDivElement>(document.createElement('div'));
	const { pathname } = useLocation();

	const { courseCoordsArray, courseSearchNodes } = useCourseMapStore();
	const coursesForSearch = courseSearchNodes.length > 0 ? courseSearchNodes : courseCoordsArray;

	/** Parent map keeps disciplines collapsed: expand the parent row once, then poll until the card mounts, then pan. */
	function handleSelection(node: NodeEntity, postExpandWaits = 0) {
		const tracker = node?.data?.tracker;
		if (!tracker) return;

		const { duration: mapDuration } = useCourseMapStore.getState();
		const MAX_POST_EXPAND_WAITS = 12;

		const nodeElm = mapCourseCardEl(tracker);
		if (!nodeElm) {
			if (postExpandWaits === 0) {
				const ancestors = (node as any)?.ancestors?.() as NodeEntity[] | undefined;
				if (!ancestors?.length) return;
				const parentNode = ancestors[ancestors.length - 2];
				if (!parentNode?.data?.tracker) return;
				const d3Parent = mapD3NodeEl(parentNode.data.tracker);
				if (!d3Parent) return;
				useCourseMapStore.getState().setSuppressNextDisciplineFitView(true);
				d3.select(d3Parent).dispatch('click');
			}
			if (postExpandWaits < MAX_POST_EXPAND_WAITS) {
				// After search-driven expand we skip fit-all zoom; wait for discipline transition + layout only.
				const delayMs =
					postExpandWaits === 0
						? Math.round(mapDuration * 1.15)
						: Math.max(80, Math.round(mapDuration / 3));
				setTimeout(() => handleSelection(node, postExpandWaits + 1), delayMs);
			}
			return;
		}

		const {
			zoom: zoomRef,
			d3InstanceRef: svgRef,
			courseCoordsArray: coords,
		} = useCourseMapStore.getState();
		const z = zoomRef.current;
		const svgNode = svgRef.current;
		if (!z || !svgNode) {
			return;
		}

		nodeElm.querySelector('.course-backdrop')?.classList.add('active-node');
		// Prefer live layout from the visible map; search-index nodes can be stale while collapsed.
		const layoutNode = coords.find((n) => n.data.tracker === tracker) ?? node;
		const translateX = layoutNode.y;
		const translateY = layoutNode.x - rectNode.height / 2;
		const xCoords = (translateX - 200 - 180) * -1;
		const yCoords = translateY * -1 + 90;

		const panMs = Math.round(mapDuration * 1.8);
		z.transform(
			svgNode
				.interrupt()
				.transition()
				.ease(d3.easeCubicInOut)
				.on('end', () => {
					setTimeout(() => {
						d3.select('#course-map').selectAll('.course-backdrop').classed('active-node', false);
					}, 2000);
				})
				.duration(panMs) as unknown as d3.Selection<SVGSVGElement, unknown, null, undefined>,
			d3.zoomIdentity.translate(xCoords, yCoords).scale(1)
		);
	}

	const [showSuggestions, setShowSuggestions] = useState(false);
	const [userInput, setUserInput] = useState('');
	const [disciplineFilter, setDisciplineFilter] = useState('');
	const disciplinesNames = useMemo(
		() => dropdownOptions?.map((d) => d.name) ?? [],
		[dropdownOptions]
	);

	const filteredCourses = useMemo(() => {
		return coursesForSearch?.filter(
			(c: NodeEntity) =>
				![`${import.meta.env.VITE_ORG}`, 'Elevate', ...disciplinesNames].includes(c.data.name) &&
				!isSpacerMapNode(c.data.name)
		);
	}, [coursesForSearch, disciplinesNames]);

	const [suggestionsArray, setSuggestionsArray] = useState<any[]>();

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset filters on navigation; pathname is a deliberate dependency though unused in the body.
	useEffect(() => {
		setUserInput('');
		setShowSuggestions(false);
		setDisciplineFilter('');
		setSuggestionsArray(undefined);
	}, [pathname]);

	const options = useMemo(
		() => ({
			useExtendedSearch: true,
			threshold: 0.3,
			findAllMatches: true,
			shouldSort: true,
			ignoreLocation: true,
			keys: ['data.name'],
		}),
		[]
	);

	const fuse = useMemo(() => new Fuse(filteredCourses || [], options), [filteredCourses, options]);

	const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
		const userInput = e.currentTarget.value;

		const results = fuse.search(userInput) as Fuse.FuseResult<any>[];
		let filteredResults = results;
		if (disciplineFilter !== '') {
			filteredResults = results.filter((x) => x.item.data.discipline === disciplineFilter);
		}
		if (e.currentTarget.value !== '') {
			setShowSuggestions(true);
		}
		if (e.currentTarget.value === '') {
			setShowSuggestions(false);
		}
		setSuggestionsArray(filteredResults.map((x) => x.item));
		setUserInput(e.currentTarget.value);
	};

	const handleOnClick = (e: React.MouseEvent<HTMLElement>, node: NodeEntity) => {
		document.querySelector('.initial-node')?.classList.remove('stop-zoom');
		setShowSuggestions(false);
		setUserInput(e.currentTarget.innerText);
		handleSelection(node);
	};

	const handleOnKeyDown = (e: React.KeyboardEvent) => {
		// User pressed the enter key
		const searchValue = (e.target as HTMLInputElement).value;
		if (e.code === 'Enter') {
			if (userInput === '') return;
			const currentCourse = suggestionsArray?.find((c) => c.data.name === searchValue);
			if (currentCourse) {
				handleSelection(currentCourse);
			}
		}
	};

	function clearSelect() {
		setUserInput('');
		setShowSuggestions(false);
	}

	interface OptionType {
		value: string;
		id: string;
		label: string;
	}

	const customStyles: StylesConfig<OptionType, false> = {
		...themeSelectStyles,
		control: (provided, state) => ({
			...provided,
			...(themeSelectStyles as any).control(provided, state),
			width: '250px',
		}),
	};

	const formattedList = (dropdownOptions: DisciplineWithCourses[]) => {
		return [...new Set(dropdownOptions)].map((item) => {
			return {
				value: item.name,
				label: item.name,
				id: item.id,
			};
		}) as OptionType[];
	};

	useOutsideClick(courseListRef, () => {
		setShowSuggestions(false);
	});

	return (
		<StyledWrapper className="map-search-bar" data-testid="mapFilters">
			<StyledAutoComplete ref={courseListRef}>
				<input
					tabIndex={0}
					placeholder="Search Course Map"
					type="text"
					onChange={handleOnChange}
					onKeyDown={handleOnKeyDown}
					value={userInput}
				/>
				{userInput !== '' && (
					<button type="button" onClick={clearSelect}>
						<svg
							aria-hidden="true"
							width="8"
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="currentColor"
						>
							<path d="M23.954 21.03l-9.184-9.095 9.092-9.174L21.03-.046l-9.09 9.179L2.764.045l-2.81 2.81L9.14 11.96.045 21.144l2.81 2.81 9.112-9.192 9.18 9.1z" />
						</svg>
						<span className="screen-reader-text">Clear Dropdown</span>
					</button>
				)}
				{showSuggestions && (
					<ul className="suggestions">
						{suggestionsArray?.map((suggestion: any) => (
							<li
								aria-label={suggestion.data.name}
								aria-hidden
								key={`suggestion-${suggestion.data.tracker}-${suggestion.depth}-${suggestion.height}`}
								onClick={(e: MouseEvent<HTMLElement>) => handleOnClick(e, suggestion)}
							>
								{suggestion.data.name}
							</li>
						))}
						{suggestionsArray?.length === 0 && (
							<li>
								<span>No matching courses found.</span>
							</li>
						)}
					</ul>
				)}
			</StyledAutoComplete>
			{dropdownOptions && (
				<Select
					key={`map-filter-discipline-${pathname}`}
					data-testid="filterSelect"
					styles={customStyles}
					placeholder="Filter Search"
					onChange={(option) => setDisciplineFilter(`${option?.value}`)}
					isClearable
					options={formattedList(dropdownOptions)}
					noOptionsMessage={() => 'No Disciplines Found'}
				/>
			)}
		</StyledWrapper>
	);
};

export default MapFilters;
